import React, { useEffect, useState } from 'react';
import { getAccentColor } from '../lib/jlfcTheme';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Loader,
  Badge,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconDatabase, IconLogin, IconMail, IconArrowLeft } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import classes from './LoginPage.module.css';
import { clearAuthSession } from '../lib/api';
import { HOME_APPLIED_KEY, resolveHomeRoute } from '../lib/moduleAccess';
import { isMobilePhone } from '../lib/device';
import MobileUnsupportedNotice from '../components/MobileUnsupportedNotice';
import LoginHeroCarousel from './LoginHeroCarousel';

const LOGO_URL = `${process.env.PUBLIC_URL || ''}/jfl-logo.png`;
const LOGO_MASK = { WebkitMaskImage: `url(${LOGO_URL})`, maskImage: `url(${LOGO_URL})` };

export default function LoginPage({ onLogin }) {
  const mobileBlocked = isMobilePhone();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('session') === 'expired';
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [dbStatus, setDbStatus] = useState({ checking: true, ok: false });

  const form = useForm({
    initialValues: { login: '', password: '' },
    validate: {
      login: (v) => (v.trim().length > 0 ? null : 'Identifiant requis'),
      password: (v) =>
        mode === 'login' && v.length === 0 ? 'Mot de passe requis' : null,
    },
  });

  const forgotForm = useForm({
    initialValues: { login: '' },
    validate: {
      login: (v) => {
        const t = v.trim();
        if (!t) return 'Identifiant requis';
        if (!/^\S+@\S+\.\S+$/.test(t)) return 'Saisissez votre adresse e-mail (identifiant)';
        return null;
      },
    },
  });

  useEffect(() => {
    if (sessionExpired || mobileBlocked) clearAuthSession();
  }, [sessionExpired, mobileBlocked]);

  useEffect(() => {
    fetch('/api/ping')
      .then((r) => r.json())
      .then((d) => setDbStatus({ checking: false, ok: Boolean(d.success) }))
      .catch(() => setDbStatus({ checking: false, ok: false }));
  }, []);

  const handleSubmit = form.onSubmit(async (values) => {
    setLoading(true);
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('jlfc_token', data.token);
        notifications.show({
          title: 'Bienvenue',
          message:
            data.user.user_type === 'client' || data.user.role === 'client'
              ? data.user.client_label || 'Espace client'
              : data.user.exploitant
                ? data.user.exploitant.raison_sociale
                : `Connecté en tant que ${data.user.login}`,
          color: getAccentColor(),
        });
        onLogin(data.user);
        sessionStorage.setItem(HOME_APPLIED_KEY, '1');
        const home = resolveHomeRoute(data.user);
        navigate(home && home !== '/' ? home : '/', { replace: true });
      } else {
        notifications.show({
          title: data.code === 'LICENCE_EXPIRED' ? 'Licence expirée' : 'Connexion refusée',
          message: data.message || 'Erreur',
          color: 'red',
        });
      }
    } catch {
      notifications.show({
        title: 'Erreur',
        message: 'Impossible de joindre le serveur',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  });

  const handleForgotSubmit = forgotForm.onSubmit(async (values) => {
    setLoading(true);
    try {
      const res = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: values.login.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setForgotSent(true);
        notifications.show({
          title: 'Demande enregistrée',
          message: data.message,
          color: getAccentColor(),
        });
      } else {
        notifications.show({
          title: 'Erreur',
          message: data.message || 'Envoi impossible',
          color: 'red',
        });
      }
    } catch {
      notifications.show({
        title: 'Erreur',
        message: 'Impossible de joindre le serveur',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  });

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.pageBrand} aria-label="JFL Contrôle">
        <Box className={classes.pageBrandInner}>
          <Box className={classes.pageBrandLogoWrap}>
            <Box className={classes.pageBrandLogoWhite} style={LOGO_MASK} aria-hidden="true" />
            <img src={LOGO_URL} alt="JFL Contrôle" className={classes.pageBrandLogo} />
          </Box>
        </Box>
      </Box>

      <Box className={classes.hero}>
        <LoginHeroCarousel />
      </Box>

      <Box className={classes.formSide}>
        <Paper className={classes.formCard} radius="lg" p="xl" shadow="md" withBorder>
          <Stack gap="lg">
            <div>
              <Title order={2} size="h3">
                {mode === 'login' ? 'Connexion' : 'Mot de passe oublié'}
              </Title>
              {mode === 'forgot' ? (
                <Text c="dimmed" size="sm" mt={4}>
                  Saisissez votre identifiant (adresse e-mail) pour recevoir un lien.
                </Text>
              ) : null}
            </div>

            {mobileBlocked ? (
              <MobileUnsupportedNotice />
            ) : null}

            {sessionExpired && !mobileBlocked ? (
              <Alert color="orange" title="Session expirée">
                Votre session a expiré. Veuillez vous reconnecter.
              </Alert>
            ) : null}

            {!mobileBlocked ? (
            <>
            <Group gap="xs">
              <IconDatabase size={16} />
              {dbStatus.checking ? (
                <Loader size="xs" />
              ) : (
                <>
                  <span
                    className={classes.statusDot}
                    style={{ background: dbStatus.ok ? '#12b886' : '#fa5252' }}
                  />
                  <Text size="xs" c="dimmed">
                    {dbStatus.ok ? 'Base de données connectée' : 'Base de données indisponible'}
                  </Text>
                </>
              )}
            </Group>

            {!dbStatus.checking && !dbStatus.ok && (
              <Alert color="red" variant="light" title="Serveur SQL">
                Vérifiez que MAMP MySQL tourne (port 8889) et que les migrations sont à jour.
              </Alert>
            )}

            {mode === 'login' ? (
              <form onSubmit={handleSubmit}>
                <Stack gap="md">
                  <TextInput
                    label="Identifiant"
                    placeholder="votre@email.fr"
                    radius="md"
                    size="md"
                    autoComplete="username"
                    {...form.getInputProps('login')}
                  />
                  <PasswordInput
                    label="Mot de passe"
                    placeholder="••••••••"
                    radius="md"
                    size="md"
                    autoComplete="current-password"
                    {...form.getInputProps('password')}
                  />
                  <Group justify="flex-end">
                    <Anchor
                      component="button"
                      type="button"
                      size="sm"
                      onClick={() => {
                        setMode('forgot');
                        setForgotSent(false);
                        forgotForm.setFieldValue('login', form.values.login);
                      }}
                    >
                      Mot de passe oublié ?
                    </Anchor>
                  </Group>
                  <Button
                    type="submit"
                    size="md"
                    radius="md"
                    loading={loading}
                    leftSection={<IconLogin size={18} />}
                    fullWidth
                  >
                    Se connecter
                  </Button>
                </Stack>
              </form>
            ) : (
              <form onSubmit={handleForgotSubmit}>
                <Stack gap="md">
                  {forgotSent ? (
                    <Alert color={getAccentColor()} variant="light" title="Vérifiez votre boîte mail">
                      Si cet identifiant est connu, un e-mail de réinitialisation a été envoyé.
                      En développement, le lien s&apos;affiche aussi dans la console du serveur.
                    </Alert>
                  ) : null}
                  <TextInput
                    label="Identifiant (e-mail)"
                    placeholder="votre@email.fr"
                    radius="md"
                    size="md"
                    autoComplete="username"
                    {...forgotForm.getInputProps('login')}
                  />
                  <Button
                    type="submit"
                    size="md"
                    radius="md"
                    loading={loading}
                    leftSection={<IconMail size={18} />}
                    fullWidth
                    disabled={forgotSent}
                  >
                    Envoyer le lien
                  </Button>
                  <Anchor
                    component="button"
                    type="button"
                    size="sm"
                    style={{ alignSelf: 'center' }}
                    onClick={() => {
                      setMode('login');
                      setForgotSent(false);
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <IconArrowLeft size={14} />
                      Retour à la connexion
                    </span>
                  </Anchor>
                </Stack>
              </form>
            )}
            </>
            ) : null}

            <Badge variant="light" color="gray" size="sm" style={{ alignSelf: 'center' }}>
              JLFC · Mantine
            </Badge>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
